import { cn } from '@neo/lib/utils'
import { useStore } from '@xyflow/react'
import { useCallback, useMemo } from 'react'
import { useTypedReactFlow } from '../../../hooks/use-react-flow'
import { useFlowStore } from '../../../store'
import type { GenericNode } from '../../../types'
import { toTestId } from '../constants'
import { getMentionableNodes } from '../mentionable-nodes'

// Node names never contain NUL, so the joined key round-trips losslessly.
const NAME_SEPARATOR = '\u0000'

interface MessageMentionRendererProps {
  content: string
  className?: string
}

export const MessageMentionRenderer = ({ content, className }: MessageMentionRendererProps) => {
  const { getNodes } = useTypedReactFlow()

  // The name set as one string key: mention parsing must follow node
  // add/remove/rename, but drags/selection/viewport churn the react-flow store
  // constantly and must not re-render (let alone re-parse) every user bubble.
  const mentionNamesKey = useStore((state) =>
    getMentionableNodes((state.nodes as GenericNode[]) ?? [])
      .map((node) => node.name)
      .join(NAME_SEPARATOR),
  )

  // Flow-store state is read at CLICK time, not render time: the parsed parts
  // below are memoized, so a subscribed selectedNodeID would be captured stale
  // inside the button closures (the toggle bug this component had before).
  const handleMentionClick = useCallback(
    (nodeName: string) => {
      const matchedNode = getMentionableNodes(getNodes() || []).find(
        (node) => node.name === nodeName,
      )
      if (!matchedNode) return
      const { selectedNodeID, selectNode, unselectNode } = useFlowStore.getState()
      if (selectedNodeID === matchedNode.id) {
        unselectNode()
      } else {
        selectNode(matchedNode.id)
      }
    },
    [getNodes],
  )

  const rendered = useMemo(() => {
    const nodeNames = (mentionNamesKey ? mentionNamesKey.split(NAME_SEPARATOR) : []).sort(
      (a, b) => b.length - a.length,
    )

    if (nodeNames.length === 0) {
      return content
    }

    const parts: (string | JSX.Element)[] = []
    let remaining = content
    let keyIndex = 0

    while (remaining.length > 0) {
      const atIndex = remaining.indexOf('@')

      if (atIndex === -1) {
        parts.push(remaining)
        break
      }

      if (atIndex > 0) {
        parts.push(remaining.slice(0, atIndex))
      }

      const textAfterAt = remaining.slice(atIndex + 1)
      let foundNodeName: string | null = null

      for (const nodeName of nodeNames) {
        if (textAfterAt.startsWith(nodeName)) {
          const charAfterName = textAfterAt[nodeName.length]
          const isWordBoundary =
            charAfterName === undefined || /[\s.,!?;:'")}\]>]/.test(charAfterName)
          if (isWordBoundary) {
            foundNodeName = nodeName
            break
          }
        }
      }

      if (foundNodeName) {
        parts.push(
          <button
            key={`mention-${keyIndex++}`}
            type="button"
            onClick={() => handleMentionClick(foundNodeName)}
            className={cn(
              'py-0.5 px-1.5 bg-link/10 text-link rounded-sm inline-flex items-center',
              'cursor-pointer hover:bg-link/20 transition-colors',
            )}
            data-testid={`button-mention-${toTestId(foundNodeName)}`}
          >
            @{foundNodeName}
          </button>,
        )
        remaining = textAfterAt.slice(foundNodeName.length)
      } else {
        parts.push('@')
        remaining = textAfterAt
      }
    }

    return parts.length > 0 ? parts : content
  }, [content, mentionNamesKey, handleMentionClick])

  return <div className={cn('text-p-400 whitespace-pre-wrap', className)}>{rendered}</div>
}
